import { RequireScope } from "@/components/require-scope";
import { GatewayCard } from "@/components/mcp/GatewayCard";
import { OrganizationGatewayCard } from "@/components/mcp/OrganizationGatewayCard";
import { useOrganizationGatewayEntry } from "@/components/mcp/useOrganizationGatewayEntry";
import {
  MCPServerCard,
  MCPServerCardSkeleton,
} from "@/components/mcp/MCPServerCard";
import {
  listedMcpServers,
  mcpServerDisplayName,
} from "@/components/mcp/mcp-server-kind";
import {
  GatewayTableRow,
  MCPServerTableRow,
  OrganizationGatewayTableRow,
  MCPTableRowSkeleton,
} from "@/components/mcp/mcp-table-rows";
import { Page } from "@/components/page-layout";
import { DotTable } from "@/components/ui/DotTable";
import { useViewMode } from "@/components/ui/ViewToggle/use-view-mode";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { useProjectSlugForRequests } from "@/contexts/Sdk";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { useRoutes } from "@/routes";
import { useMcpServers } from "@gram/client/react-query/mcpServers.js";
import { useMcpEndpoints } from "@gram/client/react-query/mcpEndpoints.js";
import { useMetaMcpServers } from "@gram/client/react-query/metaMcpServers.js";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Outlet, useSearchParams } from "react-router";
import { McpTabs } from "./McpTabs";
import { MCPEmptyState } from "./MCPEmptyState";
import {
  accessibleByFilterOptions,
  useFilterState as useMcpDimensionFilters,
  type FilterValue,
} from "@/components/filters";
import {
  gatewayFacets,
  hasActiveMcpFilters,
  matchesMcpFilters,
  mcpServerFacets,
  MCP_FILTERS,
  MCP_FILTER_OPTIONS,
  pluginFilterOptions,
  pluginMembership,
} from "./mcp-filter-schema";
import { useAccessibleServerIds } from "./use-accessible-by";
import { usePlugins } from "@gram/client/react-query/plugins.js";
import { useMembers } from "@gram/client/react-query/members.js";
import { useSession } from "@/contexts/Auth";

export function MCPRoot(): JSX.Element {
  return <Outlet />;
}

export const MCPPage = (): JSX.Element => {
  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs />
      </Page.Header>
      <Page.Body>
        <RequireScope scope={["mcp:read", "mcp:write"]} level="page">
          <Page.Section>
            <Page.Section.Title>MCP Servers</Page.Section.Title>
            <Page.Section.Description className="max-w-2xl">
              The servers your agents can reach, what they are built from, and
              the versions those arrive in.
            </Page.Section.Description>
          </Page.Section>
          <McpTabs active="servers" />
          <MCPOverview />
        </RequireScope>
      </Page.Body>
    </Page>
  );
};

function MCPOverview() {
  const routes = useRoutes();

  // These listing fetches are non-critical: degrade to the last good (or empty)
  // data with an inline indicator instead of throwing to the page error
  // boundary and replacing the whole screen. Key them by project so a tolerated
  // failure can't leave another project's rows on screen after a switch.
  const gramProject = useProjectSlugForRequests();
  const {
    data: mcpServersResult,
    isLoading: isLoadingMcpServers,
    isFetching: isFetchingMcpServers,
    isError: isMcpServersError,
    refetch: refetchMcpServers,
  } = useMcpServers({ gramProject }, undefined, {
    throwOnError: false,
  });
  // Gateways (meta MCP servers) are behind a rollout flag: opt-in, so an
  // unresolved flag keeps them hidden. The flag gates discoverability only —
  // the backend enforces mcp:read/mcp:write regardless.
  const gatewayFlag = useFeatureFlag(FEATURE_FLAGS.gatewayEndpoints);
  const gatewaysEnabled = gatewayFlag.status === "enabled";
  const {
    data: gatewaysResult,
    isLoading: isLoadingGateways,
    isFetching: isFetchingGateways,
    isError: isGatewaysError,
    refetch: refetchGateways,
  } = useMetaMcpServers({ gramProject }, undefined, {
    throwOnError: false,
    enabled: gatewaysEnabled,
  });
  const {
    data: endpointsResult,
    isLoading: isLoadingEndpoints,
    isFetching: isFetchingEndpoints,
    isError: isEndpointsError,
    refetch: refetchEndpoints,
  } = useMcpEndpoints({ gramProject }, undefined, {
    throwOnError: false,
    enabled: gatewaysEnabled,
  });
  // Plugin membership only drives the "Included in plugins" filter, so a failed
  // fetch degrades to an empty option list rather than breaking the listing.
  const { data: pluginsResult, refetch: refetchPlugins } = usePlugins(
    undefined,
    undefined,
    { throwOnError: false },
  );
  const handleRefresh = () => {
    void refetchMcpServers();
    void refetchPlugins();
    // The reach reads and the member list back the "Accessible by" control, so
    // a grant, plugin assignment or membership change would otherwise stay
    // invisible here until some other refetch happened to run.
    void refetchMembers();
    refetchReach();
    if (gatewaysEnabled) {
      void refetchGateways();
      void refetchEndpoints();
    }
  };
  const mcpServers = useMemo(
    () => listedMcpServers(mcpServersResult?.mcpServers ?? []),
    [mcpServersResult],
  );
  const gateways = useMemo(
    () => gatewaysResult?.metaMcpServers ?? [],
    [gatewaysResult],
  );
  const endpointsByGatewayId = useMemo(() => {
    const grouped = new Map<
      string,
      NonNullable<typeof endpointsResult>["mcpEndpoints"]
    >();
    for (const endpoint of endpointsResult?.mcpEndpoints ?? []) {
      if (!endpoint.metaMcpServerId) continue;
      const endpoints = grouped.get(endpoint.metaMcpServerId) ?? [];
      endpoints.push(endpoint);
      grouped.set(endpoint.metaMcpServerId, endpoints);
    }
    return grouped;
  }, [endpointsResult]);

  // The search term lives in the URL so it survives navigating to a server and
  // back again.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set("q", value);
          } else {
            next.delete("q");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [viewMode, setViewMode] = useViewMode();
  const mcpFilters = useMcpDimensionFilters(MCP_FILTERS);

  const plugins = useMemo(() => pluginsResult?.plugins ?? [], [pluginsResult]);
  const membership = useMemo(() => pluginMembership(plugins), [plugins]);

  // Who the org has, for the "Accessible by" options, and what each selected
  // person can actually reach. Both are org reads, so a viewer without them
  // simply gets no people to pick from rather than a broken control.
  const {
    data: membersResult,
    refetch: refetchMembers,
    isFetching: isFetchingMembers,
  } = useMembers(undefined, undefined, { throwOnError: false });
  const { user } = useSession();
  const members = useMemo(() => membersResult?.members ?? [], [membersResult]);
  const {
    serverIds: reachableServerIds,
    isLoading: isLoadingReach,
    isFetching: isFetchingReach,
    isError: isReachError,
    refetch: refetchReach,
  } = useAccessibleServerIds(mcpFilters.values.accessibleBy);

  // The reach read gates the rows just as the listings do: with an active
  // "Accessible by" filter and no answer yet, every row is filtered out, and
  // rendering that as "no servers match" states a result we do not have.
  const isLoading =
    isLoadingMcpServers ||
    isLoadingGateways ||
    isLoadingEndpoints ||
    isLoadingReach;

  const hasRefreshError =
    isMcpServersError || isGatewaysError || isEndpointsError || isReachError;

  // A failed reach read with the filter on leaves every row excluded for a
  // reason that is not the filter's answer, so the listing says so outright
  // rather than rendering an empty result as a finding.
  //
  // Only when there is no answer at all, though: a background refetch that
  // fails over a cached set is still filtering correctly, and replacing those
  // rows would hide a true result. That case keeps its rows and says it is
  // stale through the refresh-failed badge instead.
  const reachUnanswered =
    isReachError &&
    mcpFilters.values.accessibleBy.length > 0 &&
    reachableServerIds === undefined;

  // Declared after the reads it names: the spinner has to outlast the slowest
  // of them, and a refresh that stopped spinning while the reach or member
  // read was still in flight would look finished before it was.
  const isRefreshing =
    isFetchingMcpServers ||
    isFetchingGateways ||
    isFetchingEndpoints ||
    isFetchingMembers ||
    isFetchingReach;

  const filterOptions = useMemo(
    () => ({
      ...MCP_FILTER_OPTIONS,
      plugins: pluginFilterOptions(plugins),
      accessibleBy: accessibleByFilterOptions(members, user.id),
    }),
    [plugins, members, user.id],
  );

  const filteredMcpServers = useMemo(() => {
    const query = search.toLowerCase();
    return [...mcpServers]
      .filter((listed) => {
        const { server } = listed;
        if (
          !matchesMcpFilters(
            mcpServerFacets(listed, membership),
            mcpFilters.values,
            reachableServerIds,
          )
        )
          return false;
        if (!query) return true;
        return [
          mcpServerDisplayName(server),
          server.slug,
          server.toolset?.slug,
        ].some((value) => value?.toLowerCase().includes(query));
      })
      .sort((a, b) =>
        mcpServerDisplayName(a.server).localeCompare(
          mcpServerDisplayName(b.server),
        ),
      );
  }, [mcpServers, search, mcpFilters.values, membership, reachableServerIds]);

  const filteredGateways = useMemo(() => {
    const query = search.toLowerCase();
    return [...gateways]
      .filter((gateway) => {
        if (
          !matchesMcpFilters(
            gatewayFacets(),
            mcpFilters.values,
            reachableServerIds,
          )
        )
          return false;
        if (!query) return true;
        return gateway.name.toLowerCase().includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [gateways, search, mcpFilters.values, reachableServerIds]);

  // Show the filter bar once there's anything to filter. Filters can drive the
  // result set to empty on their own, so the no-matches state must consider an
  // active filter, not just a search query.
  const hasItems = mcpServers.length + gateways.length > 0;
  // Also shown when the list failed to load: the toolbar carries the retry and
  // "Add new", which are exactly what you need when nothing came back.
  const showFilters = !isLoading && (hasItems || hasRefreshError);
  // The Organization Gateway is the organization's, not one of this project's servers,
  // so it leads the unfiltered list and steps aside once the list is narrowed.
  const organizationGateway = useOrganizationGatewayEntry();
  const showOrganizationGateway =
    organizationGateway.show &&
    search === "" &&
    !hasActiveMcpFilters(mcpFilters.values);
  const organizationGatewayCard = showOrganizationGateway && (
    <OrganizationGatewayCard isOff={organizationGateway.isOff} />
  );
  const showNoMatches =
    !isLoading &&
    (search !== "" || hasActiveMcpFilters(mcpFilters.values)) &&
    filteredMcpServers.length === 0 &&
    filteredGateways.length === 0;

  const newMcpServerButton = (
    <RequireScope scope="mcp:write" level="component">
      {/* h-10 matches the toolbar's own controls, which Toolbar.Actions
          leaves to its children to size. */}
      <Button size="sm" className="h-10" onClick={() => routes.mcp.add.goTo()}>
        <Button.LeftIcon>
          <Plus />
        </Button.LeftIcon>
        <Button.Text>Add new</Button.Text>
      </Button>
    </RequireScope>
  );

  const refreshErrorIndicator = (
    <SimpleTooltip tooltip="We couldn't reach the server to refresh this list. Showing the most recently loaded data.">
      <Badge variant="warning">
        <Badge.LeftIcon>
          <Icon name="triangle-alert" className="inline-block" />
        </Badge.LeftIcon>
        <Badge.Text>Couldn&apos;t refresh</Badge.Text>
      </Badge>
    </SimpleTooltip>
  );

  if (
    !isLoading &&
    !hasRefreshError &&
    mcpServers.length === 0 &&
    gateways.length === 0
  ) {
    return (
      <>
        {organizationGatewayCard && (
          <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
            {organizationGatewayCard}
          </div>
        )}
        <MCPEmptyState cta={newMcpServerButton} />
      </>
    );
  }

  return (
    <>
      {/* No section heading or description: the page title above the tabs
          already says what this list is, and repeating it pushed the servers
          themselves below the fold. */}
      {/* Deliberately not a Page.Section: its top margin put this toolbar
          12px below where the sibling tabs put theirs, so switching tabs
          nudged the controls. */}
      <div>
        {showFilters && (
          <Page.Toolbar className="mb-8">
            <Page.Toolbar.Search
              value={search}
              onChange={setSearch}
              placeholder="Search MCP servers..."
            />
            <Page.Toolbar.Filters
              schema={MCP_FILTERS}
              values={mcpFilters.values}
              optionsById={filterOptions}
              onChange={
                mcpFilters.setValue as (id: string, value: FilterValue) => void
              }
              onClear={mcpFilters.clearValue as (id: string) => void}
              onClearAll={mcpFilters.clearAll}
            />
            <Page.Toolbar.ViewAs value={viewMode} onChange={setViewMode} />
            <Page.Toolbar.Refresh
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            />
            {/* The one thing you come here to do sits with the controls that
                  filter what you are looking at. */}
            <Page.Toolbar.Actions>
              {hasRefreshError ? refreshErrorIndicator : null}
              {newMcpServerButton}
            </Page.Toolbar.Actions>
          </Page.Toolbar>
        )}
        {reachUnanswered ? (
          // The rows are empty because the reach read failed, not because
          // nothing matches. Saying "no matches" here would report a result
          // off a request that never landed.
          <Text muted className="py-8 text-center">
            Couldn’t load who can reach these servers. Retry to filter by
            access.
          </Text>
        ) : showNoMatches ? (
          <Text muted className="py-8 text-center">
            {search !== ""
              ? `No MCP servers matching “${search}”`
              : "No MCP servers match your filters"}
          </Text>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {isLoading ? (
              <>
                <MCPServerCardSkeleton />
                <MCPServerCardSkeleton />
              </>
            ) : (
              <>
                {organizationGatewayCard}
                {filteredGateways.map((gateway) => (
                  <GatewayCard key={gateway.id} gateway={gateway} />
                ))}
                {filteredMcpServers.map(({ server, kind }) => (
                  <MCPServerCard key={server.id} server={server} kind={kind} />
                ))}
              </>
            )}
          </div>
        ) : (
          <DotTable
            headers={[
              { label: "Name" },
              { label: "Kind" },
              { label: "Address" },
              // Kind-dependent: tool count for hosted servers, member count
              // for gateways, nothing yet for the other backends.
              { label: "Contents" },
            ]}
          >
            {isLoading ? (
              <>
                <MCPTableRowSkeleton />
                <MCPTableRowSkeleton />
              </>
            ) : (
              <>
                {showOrganizationGateway && (
                  <OrganizationGatewayTableRow
                    url={organizationGateway.url}
                    isOff={organizationGateway.isOff}
                  />
                )}
                {filteredGateways.map((gateway) => (
                  <GatewayTableRow
                    key={gateway.id}
                    gateway={gateway}
                    endpoints={endpointsByGatewayId.get(gateway.id) ?? []}
                    isLoadingEndpoints={isLoadingEndpoints}
                  />
                ))}
                {filteredMcpServers.map(({ server, kind }) => (
                  <MCPServerTableRow
                    key={server.id}
                    server={server}
                    kind={kind}
                  />
                ))}
              </>
            )}
          </DotTable>
        )}
      </div>
    </>
  );
}
